import { Badge, Banner, Button, Dialog } from '@cloudflare/kumo';
import { useState, useEffect } from 'react';
import { useAppToast } from '../hooks/useAppToast';
import { apiService } from '../services/api';

interface ChatwootTag {
  name: string;
  count: number;
}

interface TagMapping {
  chatwootTag: string;
  categoryId: string;
}

interface ChatwootSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ChatwootSyncModal({ isOpen, onClose, onSuccess }: ChatwootSyncModalProps) {
  const toast = useAppToast();
  const [chatwootTags, setChatwootTags] = useState<ChatwootTag[]>([]);
  const [categories, setCategories] = useState<{ id: string; nome: string }[]>([]);
  const [tagMappings, setTagMappings] = useState<TagMapping[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (isOpen) loadCategories();
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories();
      setCategories(response.categories || []);
    } catch {
      toast.error('Erro ao carregar categorias');
    }
  };

  const loadChatwootTags = async () => {
    setIsLoadingTags(true);
    try {
      const response = await apiService.getChatwootTags();
      setChatwootTags(response.tags);
      if (response.tags.length === 0) {
        toast.info('Nenhuma tag encontrada no Chatwoot');
      } else {
        toast.success(`${response.tags.length} tags encontradas no Chatwoot`);
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Erro ao carregar tags';
      if (msg.includes('Configure') || msg.includes('não configurado')) {
        toast.error('Configure o Chatwoot na página de Integrações primeiro');
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoadingTags(false);
    }
  };

  const handleTagMappingChange = (chatwootTag: string, categoryId: string) => {
    setTagMappings((prev) => {
      const existing = prev.find((m) => m.chatwootTag === chatwootTag);
      if (existing) {
        if (!categoryId) return prev.filter((m) => m.chatwootTag !== chatwootTag);
        return prev.map((m) => m.chatwootTag === chatwootTag ? { ...m, categoryId } : m);
      } else if (categoryId) {
        return [...prev, { chatwootTag, categoryId }];
      }
      return prev;
    });
  };

  const handleSync = async () => {
    if (tagMappings.length === 0) { toast.error('Selecione pelo menos uma tag'); return; }
    setIsSyncing(true);
    try {
      const response = await apiService.syncChatwootContacts(tagMappings);
      toast.success(`Sincronização concluída! ${response.imported} importados, ${response.updated} atualizados`);
      onSuccess();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Erro ao sincronizar');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClose = () => {
    setChatwootTags([]);
    setTagMappings([]);
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o: boolean) => { if (!o) (handleClose)(); }}>
<Dialog size="base" className="p-6">


      <h3 className="text-lg font-semibold mb-1">Sincronizar com Chatwoot</h3>
      <div className="mb-6" style={{ color: 'var(--text-muted)' }}>Importe contatos do Chatwoot por tags</div>

      <div className="flex flex-col gap-4">
        {/* Load Tags */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold" style={{ color: 'var(--text)' }}>Tags do Chatwoot</div>
            <div style={{ color: 'var(--text-faint)', fontSize: 12 }}>
              {chatwootTags.length > 0 ? `${chatwootTags.length} tags encontradas` : 'Clique para carregar'}
            </div>
          </div>
          <Button variant="secondary" onClick={loadChatwootTags} loading={isLoadingTags}>
            {chatwootTags.length > 0 ? 'Atualizar Tags' : 'Carregar Tags'}
          </Button>
        </div>

        {/* Instructions */}
        {chatwootTags.length === 0 && !isLoadingTags && (
          <Banner variant="default" description={<>
            Carregue as tags, associe cada tag a uma categoria e clique em Sincronizar.
          </>} />
        )}

        {/* Tag Mappings */}
        {chatwootTags.length > 0 && (
          <div className="border border-[var(--border)] rounded-md overflow-hidden">
            <div className="grid grid-cols-3 gap-4 px-4 py-2 bg-[var(--surface-2)] border-b border-[var(--border)]">
              <div className="font-semibold" style={{ fontSize: 12, color: 'var(--text)' }}>Tag</div>
              <div className="font-semibold" style={{ fontSize: 12, color: 'var(--text)' }}>Contatos</div>
              <div className="font-semibold" style={{ fontSize: 12, color: 'var(--text)' }}>Categoria</div>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-[var(--border)]">
              {chatwootTags.map((tag) => (
                <div key={tag.name} className="grid grid-cols-3 gap-4 px-4 py-3 items-center hover:bg-[var(--surface-2)]">
                  <Badge variant="blue">{tag.name}</Badge>
                  <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>{tag.count} {tag.count === 1 ? 'contato' : 'contatos'}</div>
                  <select
                    aria-label={`Categoria para ${tag.name}`}
                    value={tagMappings.find((m) => m.chatwootTag === tag.name)?.categoryId || ''}
                    onChange={(e) => handleTagMappingChange(tag.name, e.target.value)}
                    className="w-full px-2 py-1.5 border border-[var(--border-strong)] rounded text-sm text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-text)]"
                  >
                    <option value="">Não importar</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        {tagMappings.length > 0 && (
          <Banner variant="default" description={<>
            {tagMappings.length} {tagMappings.length === 1 ? 'tag selecionada' : 'tags selecionadas'} para sincronização
          </>} />
        )}

        <div className="flex gap-3 pt-2 justify-end">
          <Button variant="secondary" onClick={handleClose}>Fechar</Button>
          <Button variant="primary" onClick={handleSync} disabled={tagMappings.length === 0 || isSyncing} loading={isSyncing}>
            Sincronizar Contatos
          </Button>
        </div>
      </div>
    
</Dialog>
</Dialog.Root>
  );
}
